import { ConversationGoals, UserGoals } from '@cpd-app/core-module/models';
import { GoalsInput } from '@cpd-app/generated';

import {
  EDUCATION_GOALS_CLEARED,
  careerGoalCutsEducation
} from './career-change';

/**
 * A GOAL SET FROM VIA BLANKED EVERY GOAL IT DID NOT MENTION.
 *
 * `api.usergoals` is one row per member, and the UI's upsert writes EVERY
 * column from what it is sent -- `SET certificationid = ${goals.certificationId
 * || null}` and so on down the list. A key missing from the payload is written
 * as a null, not left alone.
 *
 * `updateGoals$` is built around that: it merges each change onto the goals on
 * file before sending, which is why `setCareerGoal$` can send three fields and
 * not destroy a school goal. `setGoalsFromAiChat$` is the ONE writer that never
 * merged. It sent the four fields Via knows about, so:
 *
 *   - "Set as career goal" in chat wiped the member's school, programme,
 *     certification, licence and Registry credential;
 *   - "Set as school goal" wiped the programme, and the same three besides.
 *
 * Silently, and on the same screen where Via had just been discussing them.
 *
 * The SERVER-side Via writer never had this problem: `UserGoalsWriterService`
 * composes its `DO UPDATE SET` from the fields it was actually given, and
 * carries a note explaining that this is why a Via turn cannot clear
 * `credential_ctid`. Only the button in the chat panel wrote through the UI
 * mutation, and only it needed the merge.
 *
 * ROUTING IT THROUGH `updateGoals$` INSTEAD WAS TRIED AND REJECTED: that effect
 * resolves the occupation as `occupation?.onetCode || goals?.onetCode`, taking
 * the occupation page the member happens to have open in preference to the one
 * they just asked for. Via is a panel ON that page, so every career goal set
 * from chat would have saved the page behind it. It also owns the ui/via split
 * that DS-T16 reports on.
 */

/** The stored fields this rule carries forward. Structural, so the model and the generated input can both satisfy it. */
type GoalsOnFile = Pick<
  UserGoals,
  | 'onetCode'
  | 'occupationTitle'
  | 'jobId'
  | 'schoolId'
  | 'schoolProgramId'
  | 'schoolProgramEdLevelId'
  | 'certificationId'
  | 'licenseId'
  | 'credentialCtid'
  | 'credentialTitle'
  | 'customOnetCode'
  | 'pathwayId'
>;

/**
 * What to send for "Via set this goal", given what is already on file.
 *
 * Only the fields Via NAMED are taken from the change. A `ConversationGoals`
 * leaves the rest undefined, and naming an undefined key here would put it
 * over the top of the stored value -- which `updateUserGoals` then sends as a
 * null. That is the same trap `setInstitutionProgramGoal$` carries its own
 * note about, reached a different way.
 */
export function viaGoalChange(
  change: ConversationGoals | null | undefined,
  onFile: GoalsOnFile | null | undefined
): GoalsInput {
  const named = change ?? {};
  const stored = onFile ?? {};

  const merged: GoalsInput = {
    onetCode: stored.onetCode ?? null,
    occupationTitle: stored.occupationTitle ?? null,
    jobId: stored.jobId ?? null,
    schoolId: stored.schoolId ?? null,
    schoolProgramId: stored.schoolProgramId ?? null,
    schoolProgramEdLevelId: stored.schoolProgramEdLevelId ?? null,
    certificationId:
      stored.certificationId === undefined || stored.certificationId === null
        ? null
        : String(stored.certificationId),
    licenseId: stored.licenseId ?? null,
    credentialCtid: stored.credentialCtid ?? null,
    credentialTitle: stored.credentialTitle ?? null,
    customOnetCode: stored.customOnetCode ?? null,
    pathwayId: stored.pathwayId ?? null
  };

  /*
   * A DIFFERENT OCCUPATION LEAVES ITS OWN LABELS BEHIND.
   *
   * `occupationTitle`, `jobId`, `customOnetCode` and `pathwayId` all describe
   * the occupation, not the member. Carrying them across a change of
   * `onetCode` would file the previous occupation's title and military job id
   * against the new one.
   *
   * CPD-661 item 8: AND THE EDUCATION GOALS GO WITH THEM. A different career
   * needs different education targets, and Via must not behave differently
   * from the Set Career Goal button beside it -- see `career-change.ts`. Any
   * school or programme Via names in the SAME change is applied below, after
   * the wipe, so "this career, at this school" still lands whole.
   */
  if (named.onetCode !== undefined && named.onetCode !== merged.onetCode) {
    if (careerGoalCutsEducation(merged.onetCode, named.onetCode)) {
      Object.assign(merged, EDUCATION_GOALS_CLEARED);
    }
    merged.onetCode = named.onetCode ?? null;
    merged.occupationTitle = null;
    merged.jobId = null;
    merged.customOnetCode = null;
    merged.pathwayId = null;
  }

  if (named.schoolId !== undefined) {
    merged.schoolId = named.schoolId ?? null;
  }

  if (named.programId !== undefined) {
    merged.schoolProgramId = named.programId ?? null;
  }

  if (named.edlevelId !== undefined) {
    merged.schoolProgramEdLevelId = named.edlevelId ?? null;
  }

  /*
   * CPD-665. A certification and a Registry credential are ONE goal, so Via
   * naming a certification clears the credential exactly as the picker does.
   * Via has no tool for a Registry credential, which is why there is no
   * mirror of this branch.
   */
  if (named.certificationId !== undefined) {
    merged.certificationId =
      named.certificationId === null ? null : String(named.certificationId);
    merged.credentialCtid = null;
    merged.credentialTitle = null;
  }

  return merged;
}
